import React, { useEffect, useState, useCallback } from "react";
import { ToxnotReactModal, ToxnotReactModalBody, ToxnotReactModalFooter } from "../ToxnotReactModal";
import { IStringLocalizer } from "../../../utilities/IStringLocalizer";
import "./styles.css";
import { ToxnotServiceClient } from "../../../store/ToxnotServiceClient";
import { InventorySdsToFileNameMap } from "../../../store/Models/Api/InventorySdsToFileNameMap";

interface SdsIngredientsImportProps {
    modalId: string;
    localizer: IStringLocalizer;
    inventoryId: string;
    onFileCountUpdate?: (count: number) => void;
    refreshParentPage?: (success: boolean) => void;
}

const SdsIngredientsImport: React.FC<SdsIngredientsImportProps> = ({ modalId, localizer, inventoryId, onFileCountUpdate, refreshParentPage }) => {
    const [fileList, setFileList] = useState<InventorySdsToFileNameMap[]>([]);
    const [selectedSdsId, setSelectedSdsId] = useState<string | null>(null);

    useEffect(() => {
        const fetchSdsFiles = async () => {
            const serviceClient = new ToxnotServiceClient();
            const apiResult = await serviceClient.inventoryGetAllSdsWithFile(inventoryId);
            const surveySdsFileList = apiResult.data?.filter(x => x.workflow === "survey") ?? [];
            setFileList(surveySdsFileList);

            if (onFileCountUpdate) {
                onFileCountUpdate(surveySdsFileList.length);
            }
        };

        fetchSdsFiles();
    }, [inventoryId, onFileCountUpdate]);

    const handleRadioChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
        setSelectedSdsId(event.target.value);
    }, []);

    const importIngredients = useCallback(async () => {
        if (!selectedSdsId) return;

        const serviceClient = new ToxnotServiceClient();
        const overWriteBomRows = true; // This is hardcoded for now

        const apiResult = await serviceClient.importSdsIngredientsIntoBom(inventoryId, selectedSdsId, "", overWriteBomRows);
        if (refreshParentPage) {
            refreshParentPage(apiResult?.result === "Ok");
        }
        
    }, [selectedSdsId, inventoryId, refreshParentPage]);

    return (
        <ToxnotReactModal
            modalId={modalId}
            customHeader={
                <div>
                    <h4 className="modal-title">{localizer.localizeString("Choose an SDS to import ingredients from")}</h4>
                    <button type="button" className="close" data-dismiss="modal" aria-label="Close">
                        <span aria-hidden="true">&times;</span>
                    </button>
                </div>
            }
        >
            <ToxnotReactModalBody>
                <p>{localizer.localizeString("This action will overwrite any existing BOM data.")}</p>
                {fileList.length > 0 ? (
                    <div className="radio-list">
                        {fileList.map(file => (
                            <div key={file.fileId} className="radio-item">
                                <input
                                    type="radio"
                                    id={file.fileId}
                                    name="sdsFile"
                                    value={file.sdsId}
                                    onChange={handleRadioChange}
                                />
                                <label htmlFor={file.fileId}>
                                    <a href={`/Api/InventoryFile?inventoryId=${inventoryId}&fileId=${file.fileId}&attachment=false`} target="_blank" rel="noopener noreferrer">
                                        {file.fileName}
                                    </a>
                                </label>
                            </div>
                        ))}
                    </div>
                ) : (
                    <p>{localizer.localizeString("No SDS files available.")}</p>
                )}
            </ToxnotReactModalBody>
            <ToxnotReactModalFooter localizer={localizer}>
                <button
                    id="sds-ingredient-modal-import"
                    type="button"
                    className="btn btn-toxnot-primary"
                    data-dismiss="modal"
                    onClick={importIngredients}
                    disabled={!selectedSdsId}
                >
                    {localizer.localizeString("Add")}
                </button>
            </ToxnotReactModalFooter>
        </ToxnotReactModal>
    );
};

export default SdsIngredientsImport;
